// The pop-out window's CONTROL channel (#1430) — deliberately separate from the cache-sync channel
// in `quotationWindowSync.helper.ts`, which is app-wide and carries only "something was mutated".
//
// This one is editor-scoped and carries intent between the two windows: who is open, and a request
// to bring the view back. Two channels rather than one union type because their lifetimes differ —
// the cache sync runs for the whole app session in every window (App.tsx), while this is opened and
// closed with the editor and only ever has two participants.
//
// Why a channel at all, when the opener holds a `Window` reference to the child?
//   - The CHILD holds no reliable reference back. `window.opener` exists, but reaching into another
//     realm's React state from it is exactly the coupling the two-realm design avoids, and it is
//     null the moment the opener navigates or reloads.
//   - The child needs to tell the opener "put the view back and re-enter the in-window split" —
//     that is a request the opener must action itself, in its own realm, through its own
//     `useSplitScreen`.
//
// What it deliberately does NOT carry: entity payloads, versions, or anything a window could mistake
// for authoritative data — see `TPeerSelection` below, which is the one exception, and only ever a
// bare kind + id. The receiving window resolves that id against its OWN already-synced cache; the
// channel never becomes a second source of truth for entity state.

export const POP_OUT_CONTROL_CHANNEL = "asq-pop-out-control"

// Every message names the OPENER it belongs to, and both ends drop anything addressed elsewhere.
//
// A BroadcastChannel is origin-wide, and the editor is reachable in as many normal tabs as the user
// likes. Without this, two editor tabs cross-talk: tab A pops out, the child announces itself, and
// tab B — which owns no child — believes it is popped out too. B's menu then disables its Close rows,
// `Auto open floor plan` and the layout menu, leaving `Return right view back` as its only way out
// of its own split — and picking it CLOSES TAB A'S POP-OUT. Found in review; the id is the fix.
//
// The opener mints one per editor session and passes it to the child in the pop-out URL, so the two
// agree without a handshake.
export type TPopOutOwner = { openerId: string }

export const POP_OUT_OWNER_PARAM = "openerID"

// `randomUUID` is unavailable on http:// origins other than localhost (it needs a secure context),
// which the dev/staging hosts are — so this falls back rather than throwing. Uniqueness only has to
// hold across concurrently-open tabs on one machine.
export const createOpenerId = (): string =>
	typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
		? crypto.randomUUID()
		: `opener-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

// A selection made in one window, broadcast so the OTHER window can auto-display its connected
// counterpart — the cross-window equivalent of the in-window smart-navigation machine
// (`interactions/useSmartNavigation.ts`), just between two REALMS instead of two panes (#1430
// review). `id` is resolved against the RECEIVING window's own cache — never treated as a payload to
// render directly — so this stays consistent with the rest of the channel carrying no authoritative
// entity state.
export type TPeerSelection = { kind: "eref" | "ecall" | "furniture"; id: number }

// What a caller passes to `post`. The `openerId` is NOT part of it — the channel stamps that on the
// way out, so a call site cannot forget it and silently broadcast to every tab on the origin.
export type TPopOutControlBody =
	// The child announcing itself, so the opener's menu can move to its popped-out state even if the
	// `window.open` return value turned out to be unusable.
	| { type: "pop-out-opened" }
	// The child is going away on its own (its close button, `Return view back`, or its `pagehide`).
	// The opener also polls `child.closed`, because a window closed from the OS title bar sends
	// nothing at all — this is the fast path, the poll is the floor.
	| { type: "pop-out-closed" }
	// "Return view back" / "Return right view back": close the pop-out and restore the in-window
	// dual view. Sent by the CHILD to the opener; the opener actions it locally when the row is
	// picked there.
	| { type: "return-view-back" }
	// The opener asking the pop-out to close itself. A belt for the `child.close()` it does first:
	// that call is the real mechanism and works, but it needs a live `Window` handle, and this is
	// reachable without one.
	| { type: "pop-out-close-requested" }
	// See `TPeerSelection` above. Sent by whichever window's own selection just changed; "Auto open
	// floor plan" gates whether the receiver acts on it, same flag the in-window machine reads.
	| { type: "peer-selection"; selection: TPeerSelection }

// What actually travels, and what a handler receives.
export type TPopOutControlMessage = TPopOutControlBody & TPopOutOwner

export type TPopOutControlHandler = (message: TPopOutControlMessage) => void

const isSupported = () => typeof BroadcastChannel !== "undefined"

const KNOWN_TYPES: ReadonlySet<TPopOutControlBody["type"]> = new Set([
	"pop-out-opened",
	"pop-out-closed",
	"return-view-back",
	"pop-out-close-requested",
	"peer-selection",
])

export type TPopOutControlChannel = {
	post: (message: TPopOutControlBody) => void
	close: () => void
}

// Open this window's end of the control channel. Returns a `post` that is safe to call after
// teardown (it swallows the "channel already closed" throw, which is the normal shape of a
// close-then-post race rather than an error worth surfacing) and a `close` that fully detaches.
//
// In a browser with no BroadcastChannel the returned `post` is a no-op: the pop-out still opens and
// still works, it just loses the fast close notification and the return-view request. That is a
// deliberate soft degradation — the feature must not be gated on an optional API.
export const openPopOutControlChannel = (openerId: string, onMessage: TPopOutControlHandler): TPopOutControlChannel => {
	if (!isSupported()) return { post: () => {}, close: () => {} }

	const channel = new BroadcastChannel(POP_OUT_CONTROL_CHANNEL)

	channel.onmessage = (event: MessageEvent<TPopOutControlMessage>) => {
		// A BroadcastChannel is reachable by anything on this origin, so validate the shape rather
		// than trusting it — an unknown type is ignored, never dispatched.
		if (!event.data?.type || !KNOWN_TYPES.has(event.data.type)) return
		// Addressed to a different editor session: not ours to act on. This is what keeps two open
		// editor tabs from driving each other's menus and closing each other's windows.
		if (event.data.openerId !== openerId) return
		onMessage(event.data)
	}

	return {
		// `openerId` is stamped here rather than at each call site, so a caller cannot forget it and
		// silently broadcast to every tab.
		post: (message) => {
			try {
				channel.postMessage({ ...message, openerId })
			} catch {
				// Channel already closed by cleanup; nothing to tell anyone.
			}
		},
		close: () => {
			channel.onmessage = null
			channel.close()
		},
	}
}
